import type {TFunction} from "i18next";

export const WAIT_UNIT_FACTORS = {minutes: 1, hours: 60, days: 1440} as const;

export type WaitUnit = keyof typeof WAIT_UNIT_FACTORS;

export interface WaitDurationParts {
    value: number | '';
    unit: WaitUnit;
}

export const isWaitUnit = (candidate: unknown): candidate is WaitUnit =>
    typeof candidate === 'string' && Object.prototype.hasOwnProperty.call(WAIT_UNIT_FACTORS, candidate);

const inferUnit = (minutes: number): WaitUnit => {
    if (minutes % WAIT_UNIT_FACTORS.days === 0) return 'days';
    if (minutes % WAIT_UNIT_FACTORS.hours === 0) return 'hours';
    return 'minutes';
};

export const splitWaitDuration = (config?: Record<string, any> | null): WaitDurationParts => {
    const minutes = Number(config?.duration);

    if (!Number.isFinite(minutes) || minutes <= 0) {
        return {value: '', unit: 'minutes'};
    }

    const stored = config?.durationUnit;
    const unit = isWaitUnit(stored) && minutes % WAIT_UNIT_FACTORS[stored] === 0 ? stored : inferUnit(minutes);

    return {value: minutes / WAIT_UNIT_FACTORS[unit], unit};
};

const UNIT_KEYS: Record<WaitUnit, {one: string; other: string}> = {
    minutes: {one: '{{count}} minute', other: '{{count}} minutes'},
    hours: {one: '{{count}} hour', other: '{{count}} hours'},
    days: {one: '{{count}} day', other: '{{count}} days'},
};

export const formatWaitDuration = (config: Record<string, any> | undefined | null, t: TFunction): string | null => {
    const {value, unit} = splitWaitDuration(config);

    if (value === '') return null;

    const keys = UNIT_KEYS[unit];
    return t(value === 1 ? keys.one : keys.other, {count: value});
};
